import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, CalendarDays, Ban } from "lucide-react";
import { TYPE_META, eventColor, iso, todayMelb, addDays, monthGrid, fmtRange, fmtDate } from "@/lib/calendar";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function EventChip({ ev, onClick, compact }) {
  const c = eventColor(ev);
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick?.(ev); }} data-testid={`cal-event-${ev.id}`}
      className={`block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[11px] font-bold transition-opacity hover:opacity-80 ${ev.cancelled ? "line-through opacity-50" : ""} ${compact ? "hidden sm:block" : ""}`}
      style={{ backgroundColor: c.soft, color: c.hex, borderLeft: `3px solid ${c.hex}` }}>
      {ev.start && <span className="mr-1 font-semibold opacity-80">{fmtRange(ev).split("–")[0]}</span>}{ev.title}
    </button>
  );
}

export function AgendaItem({ ev, onClick }) {
  const c = eventColor(ev);
  return (
    <button type="button" onClick={() => onClick?.(ev)} data-testid={`agenda-${ev.id}`}
      className="flex w-full items-start gap-3 rounded-xl border border-[#EEEAF8] bg-white p-3 text-left transition-colors hover:bg-[#F8F6FF]">
      <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: c.hex }} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm font-bold text-[#1A1530] ${ev.cancelled ? "line-through opacity-60" : ""}`}>{ev.title}</span>
        <span className="block text-xs text-[#6B6488]">{fmtDate(ev.date)} · {fmtRange(ev)} · {TYPE_META[ev.type]?.label}</span>
        {ev.cancelled && <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-rose-600"><Ban className="h-3 w-3" /> Cancelled</span>}
      </span>
    </button>
  );
}

export function Legend() {
  const items = [["Maths class", "#0EA5E9"], ["English class", "#6366F1"], ["Science class", "#10B981"], ["Mock exam", TYPE_META.mock.hex], ["Key exam date", TYPE_META.key_date.hex], ["Homework due", TYPE_META.deadline.hex]];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-testid="calendar-legend">
      {items.map(([l, h]) => <span key={l} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#524B6B]"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: h }} />{l}</span>)}
    </div>
  );
}

export default function CalendarView({ program, refreshKey, onEventClick, onDayClick }) {
  const today = todayMelb();
  const [cursor, setCursor] = useState(() => { const [y, m] = today.split("-").map(Number); return { y, m: m - 1 }; });
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [selected, setSelected] = useState(today);
  const days = useMemo(() => monthGrid(cursor.y, cursor.m), [cursor]);

  useEffect(() => {
    const params = { start: iso(days[0]), end: iso(days[41]), ...(program ? { program } : {}) };
    api.get("/calendar", { params }).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [days, program, refreshKey]);
  useEffect(() => {
    api.get("/calendar", { params: { start: today, end: addDays(today, 120), ...(program ? { program } : {}) } })
      .then((r) => setUpcoming(r.data.filter((e) => !e.cancelled).slice(0, 8))).catch(() => setUpcoming([]));
  }, [program, refreshKey, today]);

  const byDate = useMemo(() => events.reduce((a, e) => ((a[e.date] = a[e.date] || []).push(e), a), {}), [events]);
  const move = (n) => setCursor(({ y, m }) => { const d = new Date(y, m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  const dayEvents = byDate[selected] || [];

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
      <div className="ember-card overflow-hidden p-4 sm:p-6" data-testid="calendar-month">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-extrabold text-[#1A1530]" data-testid="calendar-month-label">{monthLabel}</h2>
          <div className="flex items-center gap-1.5">
            <button onClick={() => move(-1)} data-testid="calendar-prev" className="rounded-lg border border-[#E6E1F5] p-2 text-[#4B2BB8] hover:bg-[#F3F0FF]"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => { const [y, m] = today.split("-").map(Number); setCursor({ y, m: m - 1 }); setSelected(today); }} data-testid="calendar-today"
              className="rounded-lg border border-[#E6E1F5] px-3 py-1.5 text-sm font-bold text-[#4B2BB8] hover:bg-[#F3F0FF]">Today</button>
            <button onClick={() => move(1)} data-testid="calendar-next" className="rounded-lg border border-[#E6E1F5] p-2 text-[#4B2BB8] hover:bg-[#F3F0FF]"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 border-b border-[#EEEAF8] pb-2 text-center text-[11px] font-bold uppercase tracking-wider text-[#948EAB]">
          {WEEKDAYS.map((w) => <div key={w}>{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const ds = iso(d);
            const evs = byDate[ds] || [];
            const inMonth = d.getMonth() === cursor.m;
            return (
              <div key={ds} role="button" tabIndex={0} data-testid={`cal-day-${ds}`}
                onClick={() => { setSelected(ds); onDayClick?.(ds); }}
                className={`min-h-[64px] cursor-pointer border-b border-r border-[#F1EEFA] p-1 transition-colors sm:min-h-[104px] sm:p-1.5 ${inMonth ? "bg-white" : "bg-[#FAF9FE]"} ${selected === ds ? "ring-2 ring-inset ring-[#4B2BB8]" : "hover:bg-[#F8F6FF]"}`}>
                <div className={`mb-1 grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${ds === today ? "bg-[#4B2BB8] text-white" : inMonth ? "text-[#1A1530]" : "text-[#C3BDD8]"}`}>{d.getDate()}</div>
                <div className="space-y-0.5">
                  {evs.slice(0, 3).map((ev) => <EventChip key={ev.id} ev={ev} onClick={onEventClick} compact />)}
                  {evs.length > 3 && <div className="hidden px-1 text-[10px] font-bold text-[#6B6488] sm:block">+{evs.length - 3} more</div>}
                  <div className="flex flex-wrap gap-0.5 sm:hidden">{evs.map((ev) => <span key={ev.id} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: eventColor(ev).hex }} />)}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4"><Legend /></div>
        <p className="mt-2 text-[11px] text-[#948EAB]">All times are Melbourne time.</p>
      </div>

      <div className="space-y-6">
        <div className="ember-card p-5" data-testid="calendar-day-panel">
          <h3 className="mb-3 flex items-center gap-2 text-base font-extrabold text-[#1A1530]"><CalendarDays className="h-4 w-4 text-[#4B2BB8]" /> {fmtDate(selected, { weekday: "long", day: "numeric", month: "long" })}</h3>
          <div className="space-y-2">
            {dayEvents.length ? dayEvents.map((ev) => <AgendaItem key={ev.id} ev={ev} onClick={onEventClick} />)
              : <p className="text-sm text-[#948EAB]" data-testid="calendar-day-empty">Nothing scheduled.</p>}
          </div>
        </div>
        <div className="ember-card p-5" data-testid="calendar-upcoming">
          <h3 className="mb-3 text-base font-extrabold text-[#1A1530]">Coming up</h3>
          <div className="space-y-2">
            {upcoming.length ? upcoming.map((ev) => <AgendaItem key={`u-${ev.id}`} ev={ev} onClick={onEventClick} />)
              : <p className="text-sm text-[#948EAB]">No upcoming events.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
